import React, { useEffect, useState } from 'react';
import { useV3Api } from '../../api/useV3Api';
import { errorText } from '../../api/errorText';
import { whenText } from '../../drift/findingModel';
import { SEVERITIES, computerFacts, replacement } from '../../drift/hardwareModel';
import ShowCommand from '../ShowCommand';

const FACTS = 'v3/facts';
const PILL = { err: 'red', warn: 'amber', ok: 'green', none: 'gray' };           // the model's tones, as the V3 pill colours
const pillOf = (tone) => `pill pill-${PILL[tone] || 'gray'}`;

const Facts = ({ items, label }) => (
  <dl className="facts" aria-label={label}>
    {items.map((f, i) => <div key={`${f.label}-${i}`}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
  </dl>
);

// The Hardware tab of a machine: the computer, how old it is and whether it should be replaced, from the latest result of the system info script
// (GET v3/facts?capability=hardware). Nothing is judged here: the replacement verdict is what the script itself worked out.
export default function HardwareTab({ device, account }) {
  const { get } = useV3Api();
  const [state, setState] = useState({ state: 'loading' });
  const name = device.hostname || device.deviceId;

  useEffect(() => {
    let cancelled = false;
    setState({ state: 'loading' });
    get(FACTS, { account, device: name, capability: 'hardware' })
      .then((body) => {
        if (cancelled) return;
        const hardware = (((body && body.items) || [])[0] || {}).hardware;
        setState(hardware && hardware.results ? { state: 'ok', results: hardware.results, updatedUtc: hardware.updatedUtc } : { state: 'none' });
      })
      .catch((ex) => { if (!cancelled) setState({ state: 'error', error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, account, name]);

  if (state.state === 'loading') return <div className="help" role="status">Loading...</div>;
  if (state.state === 'error') return <div className="field-error" role="alert">{state.error}</div>;
  if (state.state === 'none') return <div className="help">This machine has not reported its system information yet.</div>;
  const rep = replacement(state.results);
  return (
    <div className="hardware">
      <div className="hardware-grid">
        <div>
          <h3>Computer</h3>
          <Facts items={computerFacts(state.results)} label="Computer" />
        </div>
        <div>
          <h3>Age and replacement</h3>
          {!rep && <div className="help">This machine has not reported its age or replacement assessment yet.</div>}
          {rep && (
            <>
              <div><span className={pillOf(rep.verdict.tone)}>{rep.verdict.label}</span></div>
              {rep.reasons.map((x) => <div className="help" key={x}>{x}</div>)}
              <Facts items={rep.facts} label="Age and replacement" />
              {rep.findings.length > 0 && (
                <ul className="hardware-findings" aria-label="Replacement findings">
                  {rep.findings.map((f, i) => (
                    <li key={`${f.area}-${i}`}><span className={pillOf((SEVERITIES[f.severity] || {}).tone)}>{(SEVERITIES[f.severity] || {}).label || f.severity}</span>{` ${f.area ? `${f.area}: ` : ''}${f.issue}`}</li>
                  ))}
                </ul>
              )}
              {rep.notRun.length > 0 && <div className="help">{`Not checked: ${rep.notRun.join('; ')}`}</div>}
            </>
          )}
        </div>
      </div>
      <div className="facts-note">{`System information reported ${whenText(state.updatedUtc) || 'at an unknown time'}.`}</div>
      <div className="editor-actions"><ShowCommand route={FACTS} params={{ account, device: name, capability: 'hardware' }} label="PowerShell" /></div>
    </div>
  );
}
